import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import type { AdminGuideStatusGroup } from '@/features/admin/api/admin.api';
import {
  DEFAULT_SORT,
  SORT_OPTIONS,
  STATUS_GROUP_LABEL,
  STATUS_GROUP_OPTIONS,
  type GuideSortKey,
} from './guideDirectory.utils';

interface GuideDirectoryToolbarProps {
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  /** The debounced, applied term — chips reflect what the results reflect. */
  appliedSearch: string;
  statusGroup: AdminGuideStatusGroup | '';
  onStatusGroupChange: (value: AdminGuideStatusGroup | '') => void;
  sort: GuideSortKey;
  onSortChange: (value: GuideSortKey) => void;
  onClearAll: () => void;
  total?: number;
  isFetching: boolean;
}

const controlSx = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: 'var(--radius-lg)',
    bgcolor: 'background.paper',
    fontSize: '0.875rem',
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': { borderColor: 'var(--border-dark)' },
  },
} as const;

export function GuideDirectoryToolbar({
  searchInput,
  onSearchInputChange,
  appliedSearch,
  statusGroup,
  onStatusGroupChange,
  sort,
  onSortChange,
  onClearAll,
  total,
  isFetching,
}: GuideDirectoryToolbarProps) {
  const hasFilters = Boolean(appliedSearch || statusGroup || sort !== DEFAULT_SORT);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: {
            xs: '1fr 1fr',
            md: 'minmax(0, 1fr) 190px 190px auto',
          },
          alignItems: 'center',
        }}
      >
        <TextField
          type="search"
          value={searchInput}
          onChange={(event) => onSearchInputChange(event.target.value)}
          placeholder="Search by guide name, Guide ID, email or phone..."
          inputProps={{ 'aria-label': 'Search guides', maxLength: 100 }}
          sx={{
            ...controlSx,
            gridColumn: { xs: '1 / -1', md: 'auto' },
            // The native clear button duplicates ours.
            '& input::-webkit-search-cancel-button': { display: 'none' },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => onSearchInputChange('')} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        <TextField
          select
          value={statusGroup}
          onChange={(event) => onStatusGroupChange(event.target.value as AdminGuideStatusGroup | '')}
          inputProps={{ 'aria-label': 'Filter by status' }}
          SelectProps={{ displayEmpty: true }}
          sx={controlSx}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_GROUP_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          value={sort}
          onChange={(event) => onSortChange(event.target.value as GuideSortKey)}
          inputProps={{ 'aria-label': 'Sort guides' }}
          sx={controlSx}
          SelectProps={{
            renderValue: (value) => (
              <Box component="span" sx={{ display: 'inline-flex', gap: 0.5 }}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Sort:
                </Box>
                {SORT_OPTIONS[value as GuideSortKey].label}
              </Box>
            ),
          }}
        >
          {(Object.keys(SORT_OPTIONS) as GuideSortKey[]).map((key) => (
            <MenuItem key={key} value={key}>
              {SORT_OPTIONS[key].label}
            </MenuItem>
          ))}
        </TextField>

        <Button
          onClick={onClearAll}
          disabled={!hasFilters && !searchInput}
          startIcon={<FilterAltOffOutlinedIcon fontSize="small" />}
          sx={{
            height: 40,
            px: 1.5,
            gridColumn: { xs: '1 / -1', md: 'auto' },
            justifySelf: { xs: 'stretch', md: 'start' },
            color: 'text.secondary',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            '&:hover': { color: 'text.primary', bgcolor: 'var(--border-light)' },
          }}
        >
          Clear filters
        </Button>
      </Box>

      <Box
        sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minHeight: 28 }}
        aria-live="polite"
      >
        <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
          {total == null ? 'Loading guides…' : `${total.toLocaleString('en-IN')} ${total === 1 ? 'guide' : 'guides'}`}
          {isFetching && total != null ? ' · updating…' : ''}
        </Typography>
        {appliedSearch && (
          <FilterChip label={`Search: “${appliedSearch}”`} onDelete={() => onSearchInputChange('')} />
        )}
        {statusGroup && (
          <FilterChip label={`Status: ${STATUS_GROUP_LABEL[statusGroup]}`} onDelete={() => onStatusGroupChange('')} />
        )}
        {sort !== DEFAULT_SORT && (
          <FilterChip label={`Sort: ${SORT_OPTIONS[sort].label}`} onDelete={() => onSortChange(DEFAULT_SORT)} />
        )}
      </Box>
    </Box>
  );
}

function FilterChip({ label, onDelete }: { label: string; onDelete: () => void }) {
  return (
    <Chip
      size="small"
      label={label}
      onDelete={onDelete}
      sx={{
        height: 26,
        fontWeight: 500,
        bgcolor: 'primary.50',
        color: 'primary.main',
        border: '1px solid',
        borderColor: 'primary.100',
        maxWidth: 280,
        '& .MuiChip-deleteIcon': { color: 'primary.main', opacity: 0.7, '&:hover': { opacity: 1, color: 'primary.main' } },
      }}
    />
  );
}
